import {
  ArrayNode,
  Node,
  StringNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';

export const ReferencedKeywordWidget = widget<ArrayNode, string>(
  ({ node, options }) => {
    // typescript does not know that the specific conditions have been met if this is being used
    // this check should never be stronger than the check in `useWidget` below
    if (!(
      node.schema.type === 'array' &&
      !Array.isArray(node.schema.items) &&
      typeof node.schema.items !== 'boolean'
    )) {
      return null;
    }

    const properties = node.schema.items?.properties ?? {};

    const property = Object.values(properties)[0];

    const label = typeof property !== 'boolean' ? property.title : undefined;

    const validChildren = node.children
      ?.map((c) =>
        c.type === 'object'
          ? c.children.find(
              (v): v is StringNode =>
                Object.keys(properties).includes(v.property) &&
                v.type === 'string'
            )
          : null
      )
      .filter(<T,>(c: T): c is NonNullable<T> => c != null);

    const values = validChildren
      .map((c) => c.value)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);

    return (
      <FormField
        testId="ReferencedKeywordWidget"
        widgetType="array"
        node={node}
        options={options}
      >
        <ReadOnlyInput
          description={options.description}
          label={label}
          value={values.join(', ')}
          tableLayout
        />
      </FormField>
    );
  }
);

// only use widget for readonly array types, with children that have exactly one shown property
const useWidget = (node: Node): boolean =>
  node.schema.type === 'array' &&
  !Array.isArray(node.schema.items) &&
  typeof node.schema.items !== 'boolean' &&
  !!node.schema.options?.readOnly &&
  Object.values(node.schema.items?.properties ?? {}).length === 1 &&
  (node.schema.items?.additionalProperties === false ||
    (typeof node.schema.items?.additionalProperties !== 'boolean' &&
      !!node.schema.items?.additionalProperties?.options?.hidden));

export const ReferencedKeywordWidgetPlugin: WidgetPlugin = {
  id: 'referenced-keyword-widget',
  use: useWidget,
  Widget: ReferencedKeywordWidget,
};
